import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  HStack,
  Icon,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FiEdit3 } from "react-icons/fi";
import {
  Currency,
  SourcingEventStatus,
  useUpdateSourcingEventValueMutation,
  type SourcingEventDto,
} from "../../../autogen/bff-api";
import { useApiError } from "../../../common/errors/useApiError";
import { formatDecimalForInput, parseDecimalInput } from "../../../common/input/parseDecimalInput";
import { formatEstimatedValue } from "../../sourcing-events/portal/utils/displayFormatters";
import { useCanManageSourcingEvent } from "../useCanManageSourcingEvent";

const EDITABLE_STATUSES = [
  SourcingEventStatus.Awarded,
  SourcingEventStatus.Closed,
  SourcingEventStatus.Completed,
];

export const EventValueSection = ({ event }: { event: SourcingEventDto }) => {
  const { t } = useTranslation();
  const { trigger } = useApiError();
  const [updateValue, { isLoading }] = useUpdateSourcingEventValueMutation();
  const [isEditing, setIsEditing] = useState(false);
  const [draftValue, setDraftValue] = useState("");

  const currency = event.eventValue?.currency ?? Currency.Nok;
  const canManage = useCanManageSourcingEvent(event);
  const canEdit = canManage && EDITABLE_STATUSES.includes(event.status);
  const parsedDraft = parseDecimalInput(draftValue, { allowSpaceGrouping: true });

  const save = async () => {
    if (parsedDraft.kind === "notANumber") return;
    try {
      await updateValue({
        eventId: event.id,
        updateSourcingEventValueRequest: {
          eventValue: parsedDraft.kind === "number" ? { value: parsedDraft.value, currency } : null,
        },
      }).unwrap();
      setIsEditing(false);
    } catch (e) {
      trigger(e);
    }
  };

  return (
    <Box bg="smBackground" rounded="xl" border="1px solid" borderColor="smBorder" p="6">
      <Modal isOpen={isEditing} onClose={() => setIsEditing(false)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t("Edit the procurement value")}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl isInvalid={parsedDraft.kind === "notANumber"}>
              <FormLabel>{t("Procurement value")}</FormLabel>
              <Input
                inputMode="decimal"
                autoComplete="off"
                value={draftValue}
                onChange={(e) => setDraftValue(e.target.value)}
              />
              <FormHelperText>{t("The value used in reporting, in NOK")}</FormHelperText>
              <FormErrorMessage>{t("Write the amount as a number, for example 1 234,50")}</FormErrorMessage>
            </FormControl>
          </ModalBody>
          <ModalFooter gap="3">
            <Button variant="ghost" onClick={() => setIsEditing(false)}>
              {t("Abort")}
            </Button>
            <Button
              colorScheme="teal"
              onClick={save}
              isLoading={isLoading}
              isDisabled={parsedDraft.kind === "notANumber"}
            >
              {t("Save")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <HStack justify="space-between" align="flex-start">
        <Box>
          <Text fontWeight="semibold" fontSize="sm" color="smMuted" textTransform="uppercase" mb="1">
            {t("Procurement value")}
          </Text>
          <Text fontSize="lg" fontWeight="semibold" color="smText">
            {event.eventValue?.value != null
              ? formatEstimatedValue(event.eventValue.value, currency)
              : "–"}
          </Text>
        </Box>
        {canEdit && (
          <Button
            size="sm"
            variant="outline"
            leftIcon={<Icon as={FiEdit3} />}
            onClick={() => {
              setDraftValue(event.eventValue?.value != null ? formatDecimalForInput(event.eventValue.value) : "");
              setIsEditing(true);
            }}
          >
            {t("Edit")}
          </Button>
        )}
      </HStack>
    </Box>
  );
};
